import React, { useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography } from '@mui/material';

import LocationForm from '../../LocationSetup/LocationForm';
import { useWarehouseLocationLayout, useWarehouseStorageConfig } from 'hooks/Warehouse';
import styles from '../styles';

function Locations({ warehouse_id, region, alias }) {
  const { classes } = styles();
  const { layout, loaded, reload } = useWarehouseLocationLayout(warehouse_id);
  const { storageConfig } = useWarehouseStorageConfig(warehouse_id);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const assignedAreaByLocationId = {};
  (storageConfig?.storageAreas ?? []).forEach((area) => {
    (area.locations ?? []).forEach((locationId) => {
      assignedAreaByLocationId[locationId] = area.name;
    });
  });

  const closeDialog = () => {
    setEditOpen(false);
    setSaving(false);
  };

  const handleSaved = () => {
    closeDialog();
    reload();
  };

  const defaultLayoutName = `${(alias || 'warehouse').trim().toLowerCase().replace(/\s+/g, '-')}-layout`;
  const hasLocations = Boolean(layout?.locations?.length);

  return (
    <Paper className={classes.Info} elevation={0}>
      <div className="info-header">
        <Typography variant="h5" color="secondary">
          Locations
        </Typography>
        <Button variant="contained" color="primary" onClick={() => setEditOpen(true)}>
          Manage locations
        </Button>
      </div>
      <Typography variant="caption" color="textSecondary">
        Physical locations on the warehouse floor that containers are assigned to.
      </Typography>
      {loaded && !hasLocations && (
        <Box sx={{ color: 'red' }}>
          Location layout must be set up so containers can be assigned to warehouse locations. SETUP NOW &gt;&gt;&gt;
        </Box>
      )}
      {hasLocations && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 96 }}>
            <Typography variant="h4" color="primary" sx={{ lineHeight: 1 }}>
              {layout.locations.length}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              locations defined
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
              {layout.name} ({layout.region})
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {layout.locations.map((location) => (
              <Chip key={location.id} size="small" variant="outlined" label={location.name || location.id} />
            ))}
          </Box>
        </Box>
      )}
      <Dialog open={editOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle variant="h6">Locations</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            Add, rename, or remove the locations containers are assigned to. Existing ids are locked because storage
            areas reference them.
          </Typography>
          <LocationForm
            formId="location-layout-form"
            warehouseId={warehouse_id}
            region={layout?.region || region || ''}
            defaultLayoutName={layout?.name || defaultLayoutName}
            initialLocations={layout?.locations}
            forceUpdate={Boolean(layout)}
            assignedAreaByLocationId={assignedAreaByLocationId}
            onSuccess={handleSaved}
            onSubmittingChange={setSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="inherit" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="location-layout-form" loading={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default Locations;
